import { Button } from "@knowtex-ai/components";
import { useEffect, useState, type ReactElement } from "react";
import css from "../record.module.css";
import { getAudioTestLabel } from "./recorderUiRules";

export interface AudioTestControlProps {
  isActive: boolean;
  isStarting: boolean;
  onToggle: () => void;
}

export default function AudioTestControl({
  isActive,
  isStarting,
  onToggle,
}: AudioTestControlProps): ReactElement {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const label = getAudioTestLabel(isStarting, isActive);

  useEffect(() => {
    if (!isActive) {
      setTooltipVisible(false);
      return undefined;
    }

    setTooltipVisible(true);

    const dismissTooltip = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setTooltipVisible(false);
      }
    };

    document.addEventListener("keydown", dismissTooltip, true);
    return () => {
      document.removeEventListener("keydown", dismissTooltip, true);
    };
  }, [isActive]);

  return (
    <div
      className={`${css.audioTestControl} ${isActive ? css.audioTestControlActive : ""}`}
      data-testid="audio-test-control"
      onMouseEnter={() => setTooltipVisible(true)}
      onMouseLeave={(event) => {
        if (isActive) {
          return;
        }
        if (!event.currentTarget.contains(document.activeElement)) {
          setTooltipVisible(false);
        }
      }}
      onFocus={() => setTooltipVisible(true)}
      onBlur={(event) => {
        if (isActive) {
          return;
        }
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setTooltipVisible(false);
        }
      }}
    >
      {isActive ? (
        <span
          className={css.audioTestButtonFill}
          data-testid="audio-test-countdown-fill"
          aria-hidden="true"
          style={{ width: "100%" }}
        />
      ) : null}
      <Button
        type="button"
        variant={isActive ? "secondary-alt" : "secondary"}
        fullWidth
        disabled={isStarting}
        aria-pressed={isActive}
        aria-label={isActive ? "Stop test" : "Test Audio"}
        aria-describedby={isActive ? "audio-test-tooltip" : undefined}
        className={`${css.audioTestButton} ${isActive ? css.audioTestButtonActive : ""}`}
        onClick={onToggle}
      >
        <span className={css.audioTestButtonLabel}>{label}</span>
      </Button>
      {isActive ? (
        <span
          className={css.audioTestButtonLabelInverseClip}
          data-testid="audio-test-label-inverse"
          aria-hidden="true"
        >
          <span className={css.audioTestButtonLabelInverse}>{label}</span>
        </span>
      ) : null}
      {isActive ? (
        <div
          id="audio-test-tooltip"
          role="tooltip"
          aria-hidden={!tooltipVisible}
          className={`${css.audioTestTooltip} ${tooltipVisible ? css.audioTestTooltipVisible : ""}`}
        >
          Test Audio is running. Click to stop early; recording unlocks when it finishes.
        </div>
      ) : null}
    </div>
  );
}
